/**
 * CompanySettingsPage — ekran „Profil / Dane firmy".
 *
 * Pobiera i zapisuje dane zleceniodawcy używane w eksporcie XML pain.001.001.09.
 * Endpointy: GET/PUT `/api/company-settings`.
 *
 * Zakres MVP:
 *   - nazwa firmy, NIP, adres, IBAN, domyślny bank, domyślny format eksportu,
 *   - walidacja po stronie backendu (422 → toast + wskazanie pola),
 *   - migracja localStorage → backend przy pierwszym zapisie (fallback jeśli
 *     user miał dane w cache eksportu, ale nigdy nie otwierał tego ekranu).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Building2, Save, Loader2 } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_STATE = {
  company_name: "",
  nip: "",
  address: "",
  debtor_iban: "",
  default_bank: "mbank",
  default_export_format: "xml",
};

const CompanySettingsPage = () => {
  const [form, setForm] = useState(DEFAULT_STATE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/company-settings`, { withCredentials: true });
        if (cancelled) return;
        const data = res.data || {};
        // Fallback z localStorage tylko dla pustych pól (bootstrap dla użytkowników,
        // którzy wcześniej ustawili dane w modalu eksportu).
        const lsName = localStorage.getItem("pax_debtor_name") || "";
        const lsIban = localStorage.getItem("pax_debtor_iban") || "";
        setForm({
          company_name: data.company_name || lsName || "",
          nip: data.nip || "",
          address: data.address || "",
          debtor_iban: data.debtor_iban || lsIban || "",
          default_bank: data.default_bank || "mbank",
          default_export_format: data.default_export_format || "xml",
        });
      } catch (e) {
        if (!cancelled) toast.error("Nie udało się pobrać danych firmy.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleChange = (field) => (e) => {
    const value = e?.target?.value ?? "";
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const payload = {
        company_name: form.company_name.trim(),
        nip: form.nip.trim(),
        address: form.address.trim(),
        debtor_iban: form.debtor_iban.trim(),
        default_bank: form.default_bank,
        default_export_format: form.default_export_format,
      };
      const res = await axios.put(`${API}/company-settings`, payload, { withCredentials: true });
      const data = res.data || {};
      setForm({
        company_name: data.company_name || "",
        nip: data.nip || "",
        address: data.address || "",
        debtor_iban: data.debtor_iban || "",
        default_bank: data.default_bank || "mbank",
        default_export_format: data.default_export_format || "xml",
      });
      // Sync do localStorage — utrzymanie kompatybilności z legacy fallback w modalu.
      if (data.company_name) localStorage.setItem("pax_debtor_name", data.company_name);
      if (data.debtor_iban) localStorage.setItem("pax_debtor_iban", data.debtor_iban);
      toast.success("Dane firmy zostały zapisane.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const message = (detail && typeof detail === "object" && detail.message) || "Nie udało się zapisać danych firmy.";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-500 py-12" data-testid="company-settings-loading">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span>Wczytywanie danych firmy…</span>
      </div>
    );
  }

  return (
    <div data-testid="company-settings-page" className="max-w-2xl">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-[#0F1F3D] tracking-tight">Profil — dane firmy</h1>
        <p className="text-gray-500 mt-1 text-sm">
          Dane zleceniodawcy używane przy eksporcie płatności (m.in. XML mBank pain.001.001.09).
        </p>
      </div>

      <Card className="border border-gray-200">
        <CardHeader>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center shrink-0">
              <Building2 className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <CardTitle className="text-base text-[#0F1F3D]">Dane firmy</CardTitle>
              <CardDescription>
                Uzupełnione dane pojawią się automatycznie w oknie „Eksport do banku”.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={handleSave}>
            <div>
              <Label htmlFor="company_name" className="text-sm">Nazwa firmy</Label>
              <Input
                id="company_name"
                data-testid="company-settings-name"
                value={form.company_name}
                onChange={handleChange("company_name")}
                placeholder="np. PAX Sp. z o.o."
                maxLength={140}
                disabled={saving}
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="nip" className="text-sm">NIP</Label>
                <Input
                  id="nip"
                  data-testid="company-settings-nip"
                  value={form.nip}
                  onChange={handleChange("nip")}
                  placeholder="10 cyfr"
                  maxLength={20}
                  disabled={saving}
                  className="mt-1 font-mono"
                />
                <p className="text-xs text-gray-500 mt-1">Podstawowy format: 10 cyfr (separatory dozwolone).</p>
              </div>
              <div>
                <Label htmlFor="default_bank" className="text-sm">Domyślny bank</Label>
                <select
                  id="default_bank"
                  data-testid="company-settings-bank"
                  value={form.default_bank}
                  onChange={handleChange("default_bank")}
                  disabled={saving}
                  className="mt-1 w-full h-10 border border-gray-300 rounded-md px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200"
                >
                  <option value="mbank">mBank</option>
                </select>
              </div>
            </div>

            <div>
              <Label htmlFor="address" className="text-sm">Adres</Label>
              <Input
                id="address"
                data-testid="company-settings-address"
                value={form.address}
                onChange={handleChange("address")}
                placeholder="ul. Przykładowa 1, 00-001 Warszawa"
                maxLength={250}
                disabled={saving}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="debtor_iban" className="text-sm">Rachunek źródłowy (IBAN)</Label>
              <Input
                id="debtor_iban"
                data-testid="company-settings-iban"
                value={form.debtor_iban}
                onChange={handleChange("debtor_iban")}
                placeholder="PL61 1090 1014 0000 0712 1981 2874"
                maxLength={64}
                disabled={saving}
                className="mt-1 font-mono"
              />
              <p className="text-xs text-gray-500 mt-1">
                Znormalizujemy zapis (bez spacji, uppercase, prefix PL). Walidacja mod 97.
              </p>
            </div>

            <div>
              <Label htmlFor="default_export_format" className="text-sm">Domyślny format eksportu</Label>
              <select
                id="default_export_format"
                data-testid="company-settings-format"
                value={form.default_export_format}
                onChange={handleChange("default_export_format")}
                disabled={saving}
                className="mt-1 w-full h-10 border border-gray-300 rounded-md px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200"
              >
                <option value="xml">XML mBank (pain.001.001.09) — zalecany</option>
                <option value="elixir">TXT mBank / ELIXIR-0 TXT</option>
                <option value="csv">CSV (legacy)</option>
              </select>
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
              <Button
                type="submit"
                data-testid="company-settings-save-btn"
                disabled={saving}
                className="bg-[#0F1F3D] text-white hover:bg-[#182D5A]"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Zapisz dane firmy
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CompanySettingsPage;
